import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";

/**
 * <NextStepHero/> — single-glance "What do I do next?" banner.
 *
 * Sits at the very top of the client portal (above the dark hello hero) and
 * surfaces ONLY the client's next actionable step. When all client steps are
 * complete, switches into a celebratory state telling the client the firm
 * has the ball.
 */
export default function NextStepHero({ plan, onClick }) {
  if (!plan || !plan.steps?.length) return null;

  const clientSteps = plan.steps.filter((s) => s.action_kind !== "milestone");
  const allDone = clientSteps.length > 0 &&
                  clientSteps.every((s) => s.status === "complete");
  const nextStep = clientSteps.find((s) => s.id === plan.next_step_id)
                 || clientSteps.find((s) => s.status !== "complete");

  if (allDone || !nextStep) {
    return (
      <div
        className="bg-emerald-600 text-white"
        data-testid="next-step-hero"
      >
        <div className="mx-auto max-w-5xl px-6 py-4 flex items-center gap-3 flex-wrap">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] tracking-widest uppercase opacity-80">
              All client steps complete
            </div>
            <div className="text-base font-medium">
              Your part is done — EH Inc is now handling the rest.
            </div>
          </div>
          <span className="text-[10px] tracking-widest uppercase bg-emerald-800 px-2 py-1">
            Nothing to do
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-[#0055FF] text-white"
      data-testid="next-step-hero"
    >
      <div className="mx-auto max-w-5xl px-6 py-4 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <Sparkles className="h-4 w-4" />
          <span className="text-[10px] tracking-widest uppercase opacity-80">
            Next step
          </span>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-base sm:text-lg font-medium" data-testid="next-step-title">
            {nextStep.title}
          </div>
          <div className="text-xs sm:text-sm opacity-80 mt-0.5">
            {nextStep.description}
          </div>
        </div>
        <button
          onClick={() => onClick?.(nextStep)}
          className="shrink-0 bg-white text-[#0055FF] hover:bg-zinc-100 text-sm font-medium px-4 py-2 inline-flex items-center gap-1.5"
          data-testid="next-step-cta"
        >
          {nextStep.action_label}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
